import React, { useEffect, useRef, useState } from 'react';
import { cx } from './helpers';
import { coverFrameTime } from './trackCardUtils';
import styles from './RecommendedTracksSection.module.css';

export default function VideoCover({ src, className, onFail }) {
  const hostRef = useRef(null);
  const videoRef = useRef(null);
  const onFailRef = useRef(onFail);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    onFailRef.current = onFail;
  }, [onFail]);

  useEffect(() => {
    const node = hostRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver !== 'function') {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: '240px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const frameSrc = src.includes('#') ? src : `${src}#t=0.5`;

  useEffect(() => {
    if (!shouldLoad) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    let cancelled = false;
    setReady(false);
    let seeked = false;
    let started = false;
    const reveal = () => {
      if (!cancelled) setReady(true);
    };
    const fail = () => {
      if (!cancelled) onFailRef.current?.();
    };
    const finish = () => {
      if (seeked || cancelled) return;
      seeked = true;
      const played = video.play();
      if (played && typeof played.then === 'function') {
        played
          .then(() => {
            video.pause();
            reveal();
          })
          .catch(() => reveal());
        return;
      }
      video.pause();
      reveal();
    };

    const paintFrame = () => {
      if (cancelled || seeked || started) return;
      if (!video.videoWidth) return;
      started = true;
      const target = coverFrameTime(video.duration);
      if (target <= 0 || Math.abs(video.currentTime - target) < 0.04) {
        finish();
        return;
      }

      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        finish();
      };
      video.addEventListener('seeked', onSeeked);
      try {
        video.currentTime = target;
      } catch {
        video.removeEventListener('seeked', onSeeked);
        finish();
      }
    };

    video.muted = true;
    video.addEventListener('loadedmetadata', paintFrame);
    video.addEventListener('loadeddata', paintFrame);
    video.addEventListener('error', fail);

    const slowReveal = window.setTimeout(() => {
      if (cancelled || seeked) return;
      if (video.videoWidth) finish();
      else fail();
    }, 1800);

    return () => {
      cancelled = true;
      window.clearTimeout(slowReveal);
      video.removeEventListener('loadedmetadata', paintFrame);
      video.removeEventListener('loadeddata', paintFrame);
      video.removeEventListener('error', fail);
      video.pause();
    };
  }, [shouldLoad, src]);

  return (
    <span ref={hostRef} className={styles.coverFrame}>
      {shouldLoad ? (
        <video
          ref={videoRef}
          className={cx(className, ready && styles.coverReady)}
          src={frameSrc}
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
          aria-hidden="true"
          disablePictureInPicture
        />
      ) : null}
    </span>
  );
}
